import { useQuery } from '@tanstack/react-query';
import { formatInteger } from '@brimo/shared';
import { TextLink } from '../../../ds/components';
import { api } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import type { ChecklistResponse } from '../types';
import styles from './ChecklistSummary.module.css';

/** „3 von 23 Pflichtunterlagen · 21 offen" — required items done of all required, open = every item not done. */
export function checklistSummaryText(checklist: Pick<ChecklistResponse, 'progress'>): string {
  const { all, required } = checklist.progress;
  return `${formatInteger(required.done)} von ${formatInteger(required.total)} Pflichtunterlagen · ${formatInteger(all.total - all.done)} offen`;
}

/**
 * The compact line „Unterlagen & Checkliste" of the property record in „In Ankaufsprüfung" (Nico Kremer's
 * feedback 01.11.2026, D-368): the checklist progress of `GET /properties/:id/checklist` and the link „Zur
 * Checkliste" (wizard step 5 „Checkliste & Freigabe"). Nothing while loading, on an error or without a template.
 */
export function ChecklistSummary({ propertyId }: { propertyId: string }) {
  const checklist = useQuery({
    queryKey: [...queryRoots.property, propertyId, 'checklist'],
    queryFn: ({ signal }) => api<ChecklistResponse>(`/properties/${propertyId}/checklist`, { signal }),
    staleTime: 15_000,
  });
  const data = checklist.data;
  if (!data || data.progress.all.total === 0) return null;
  return (
    <div className={styles.checklistLine}>
      <span className={styles.checklistText}>
        <span className={styles.checklistLabel}>Unterlagen & Checkliste</span>
        <span className={styles.checklistValue}>{checklistSummaryText(data)}</span>
      </span>
      <TextLink to={`/properties/${propertyId}/wizard?step=5`} size="small">
        Zur Checkliste
      </TextLink>
    </div>
  );
}
